import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export default function SystemStatusCard({ refreshInterval = 60000 }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchSystemStatus();
    
    // Set up auto-refresh
    const intervalId = setInterval(() => {
      fetchSystemStatus();
      setLastRefresh(new Date());
    }, refreshInterval);
    
    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get('/api/alerts/system/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching system status:', error);
      toast.error('Failed to load system status');
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchSystemStatus();
    setLastRefresh(new Date());
    toast.info('Dashboard refreshed');
  };

  if (loading && !status) {
    return (
      <div className="bg-white shadow rounded p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded">
      <div className="bg-purple-l border-b border-gray-200 px-6 py-2 flex justify-between items-center">
        <h2 className="text-white font-bold tracking-wider uppercase text-md">System Status</h2>
        <div className="flex items-center">
          <span className="text-white text-xs mr-2">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button 
            onClick={handleManualRefresh}
            className="bg-white text-purple-600 hover:bg-gray-100 text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {status && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Total Alerts</div>
              <div className="text-3xl font-bold text-gray-700">{status.total_alerts}</div>
              <div className="text-gray-500 text-xs mt-2">
                {status.active_alerts} active / {status.total_alerts - status.active_alerts} inactive
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <div className="text-green-500 text-xs font-semibold uppercase mb-1">Success Rate</div>
              <div className="text-3xl font-bold text-green-700">
                {status.total_executions > 0 
                  ? Math.round((status.successful_executions / status.total_executions) * 100) 
                  : 0}%
              </div>
              <div className="text-green-500 text-xs mt-2">
                {status.successful_executions} of {status.total_executions} executions
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded p-4">
              <div className="text-red-500 text-xs font-semibold uppercase mb-1">Failure Rate</div>
              <div className="text-3xl font-bold text-red-700">
                {status.total_executions > 0 
                  ? Math.round((status.failed_executions / status.total_executions) * 100) 
                  : 0}%
              </div>
              <div className="text-red-500 text-xs mt-2">
                {status.failed_executions} of {status.total_executions} executions
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded p-4">
              <div className="text-purple-500 text-xs font-semibold uppercase mb-1">Avg. Execution Time</div>
              <div className="text-3xl font-bold text-purple-700">
                {status.average_execution_time ? status.average_execution_time.toFixed(2) : '0.00'}s
              </div>
              <div className="text-purple-500 text-xs mt-2">
                {status.total_affected_nodes} total affected nodes
              </div>
            </div>
          </div>
          
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded p-4">
            <div className="text-gray-500 text-xs font-semibold uppercase mb-2">Recent Activity</div>
            <div className="text-sm">
              <div className="flex justify-between mb-1">
                <span>Last execution:</span>
                <span className="font-medium">
                  {status.last_execution_time 
                    ? new Date(status.last_execution_time).toLocaleString() 
                    : 'No executions yet'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Last 24 hours:</span>
                <span className="font-medium">{status.executions_last_24h} executions</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 